import { pgTable, text, varchar, integer, timestamp, boolean, numeric } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Regex patterns for validating nrPorzadkowy format (used in CSV imports)
export const clientNrRegex = /^C\d+$/;
export const projectNrRegex = /^P\d+$/;
export const costNrRegex = /^K\d+$/;
export const revenueNrRegex = /^FV\d+$/;

// Client type options (Typ klienta)
export const clientTypeOptions = ["Komercyjny", "Indywidualny", "Administracyjny"] as const;
export type ClientType = typeof clientTypeOptions[number];

// Priority options (Priorytet)
export const priorityOptions = ["Strategiczny", "Wysoki", "Średni", "Niski"] as const;
export type Priority = typeof priorityOptions[number];

// Client table schema
export const clients = pgTable("clients", {
  id: varchar("id").primaryKey(),
  nrPorzadkowy: text("nr_porzadkowy").notNull(),
  nazwa: text("nazwa").notNull(),
  nip: text("nip").notNull(),
  adresSiedziby: text("adres_siedziby").notNull(),
  typKlienta: text("typ_klienta").$type<ClientType>(),
  telefon: text("telefon").notNull(),
  email: text("email").notNull(),
  opis: text("opis").notNull(),
  priorytet: text("priorytet").$type<Priority>(),
});

// Insert schema for clients (nrPorzadkowy optional for CSV imports)
export const insertClientSchema = createInsertSchema(clients).omit({
  id: true,
  nrPorzadkowy: true,
}).extend({
  nrPorzadkowy: z.string().regex(clientNrRegex, "Nr porządkowy musi być w formacie C1, C2, itp.").optional(),
  typKlienta: z.enum(clientTypeOptions).nullable().optional(),
  priorytet: z.enum(priorityOptions).nullable().optional(),
});

// Extended schema with validation
export const clientFormSchema = insertClientSchema.extend({
  nazwa: z.string().min(1, "Nazwa jest wymagana"),
  nip: z.string().min(1, "NIP jest wymagany"),
  adresSiedziby: z.string().min(1, "Adres siedziby jest wymagany"),
  typKlienta: z.enum(clientTypeOptions),
  telefon: z.string().min(1, "Telefon jest wymagany"),
  email: z.string().email("Nieprawidłowy format email"),
  opis: z.string(),
  priorytet: z.enum(priorityOptions),
});

export type InsertClient = z.infer<typeof insertClientSchema>;
export type Client = typeof clients.$inferSelect;

// Users table - for JWT authentication
export const users = pgTable("users", {
  id: varchar("id").primaryKey(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(), // bcrypt hashed
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// Refresh tokens table - for JWT refresh token rotation
export const refreshTokens = pgTable("refresh_tokens", {
  id: varchar("id").primaryKey(),
  userId: varchar("user_id").notNull(),
  token: text("token").notNull().unique(), // hashed token
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertRefreshTokenSchema = createInsertSchema(refreshTokens).omit({
  id: true,
  createdAt: true,
});

export type InsertRefreshToken = z.infer<typeof insertRefreshTokenSchema>;
export type RefreshToken = typeof refreshTokens.$inferSelect;

// Auth-related Zod schemas
export const loginSchema = z.object({
  email: z.string().email("Nieprawidłowy format email"),
  password: z.string().min(1, "Hasło jest wymagane"),
});

export const userSettingsSchema = z.object({
  firstName: z.string().min(1, "Imię jest wymagane"),
  lastName: z.string().min(1, "Nazwisko jest wymagane"),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type UserSettingsInput = z.infer<typeof userSettingsSchema>;

// Password change schema
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Aktualne haslo jest wymagane"),
  newPassword: z.string().min(8, "Nowe haslo musi miec minimum 8 znakow"),
  confirmPassword: z.string().min(1, "Potwierdzenie hasla jest wymagane"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Hasla nie sa identyczne",
  path: ["confirmPassword"],
});

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// Client history table for tracking changes
export const clientHistory = pgTable("client_history", {
  id: varchar("id").primaryKey(),
  clientId: varchar("client_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertClientHistorySchema = createInsertSchema(clientHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertClientHistory = z.infer<typeof insertClientHistorySchema>;
export type ClientHistory = typeof clientHistory.$inferSelect;

// Project type options (Typ projektu)
export const projectTypeOptions = [
  "WOD-KAN",
  "Pompa ciepła",
  "Więcej instalacji",
  "Wentylacja",
  "Przegląd techniczny",
  "Klimatyzacja",
  "PV",
  "Podłogówka",
  "Serwis (naprawy)",
  "Projektowanie",
  "Inny",
  "Administracyjny",
] as const;
export type ProjectType = typeof projectTypeOptions[number];

// Project status options (Status projektu)
export const projectStatusOptions = [
  "W przygotowaniu",
  "W trakcie",
  "Wstrzymany",
  "Zakończony",
  "Administracyjny",
] as const;
export type ProjectStatus = typeof projectStatusOptions[number];

// Responsible person options
export const responsiblePersonOptions = ["Basia", "Ala", "Grzesiek", "Michał"] as const;
export type ResponsiblePerson = typeof responsiblePersonOptions[number];

// Project kind options (Rodzaj projektu)
export const projectKindOptions = ["Zewnętrzny", "Wewnętrzny"] as const;
export type ProjectKind = typeof projectKindOptions[number];

// Projects table schema
export const projects = pgTable("projects", {
  id: varchar("id").primaryKey(),
  nrPorzadkowy: text("nr_porzadkowy").notNull(),
  nrPorzadkowyKlienta: text("nr_porzadkowy_klienta"),
  nazwaRoboczaProjektu: text("nazwa_robocza_projektu"),
  klient: text("klient"),
  typKlienta: text("typ_klienta"),
  typProjektu: text("typ_projektu").$type<ProjectType>(),
  statusProjektu: text("status_projektu").$type<ProjectStatus>(),
  rodzajProjektu: text("rodzaj_projektu").$type<ProjectKind>(),
  osobaSprzedaz: text("osoba_sprzedaz"),
  osobaWykonastwo: text("osoba_wykonastwo"),
  wartoscUmowyNetto: numeric("wartosc_umowy_netto"),
  planowanyKosztTowaru: numeric("planowany_koszt_towaru"),
  planowaneKosztyWykonastwa: numeric("planowane_koszty_wykonastwa"),
  narzutBezpieczenstwa: numeric("narzut_bezpieczenstwa"),
  planowanyZysk: numeric("planowany_zysk"),
  wartoscPracDodatkowych: numeric("wartosc_prac_dodatkowych"),
  podpisanieUmowy: boolean("podpisanie_umowy").notNull(),
  dataPodpisaniaUmowy: text("data_podpisania_umowy"),
  planowanyPoczatekRealizacji: text("planowany_poczatek_realizacji"),
  planowanyKoniecRealizacji: text("planowany_koniec_realizacji"),
  faktycznyPoczatekRealizacji: text("faktyczny_poczatek_realizacji"),
  faktycznyKoniecRealizacji: text("faktyczny_koniec_realizacji"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Preprocessor for numeric fields: accepts string or number, normalizes decimal separators
const numericPreprocess = z.preprocess(
  (val) => {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "number") return String(val);
    if (typeof val === "string") {
      // Remove thousand separators (spaces) and normalize comma to dot
      const normalized = val.replace(/\s/g, "").replace(",", ".");
      return normalized;
    }
    return val;
  },
  z.string().nullable().optional()
);

// Insert schema for projects (nrPorzadkowy optional for CSV imports)
export const insertProjectSchema = createInsertSchema(projects)
  .omit({
    id: true,
    nrPorzadkowy: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    nrPorzadkowy: z.string().regex(projectNrRegex, "Nr porządkowy musi być w formacie P1, P2, itp.").optional(),
    wartoscUmowyNetto: numericPreprocess,
    planowanyKosztTowaru: numericPreprocess,
    planowaneKosztyWykonastwa: numericPreprocess,
    narzutBezpieczenstwa: numericPreprocess,
    planowanyZysk: numericPreprocess,
    wartoscPracDodatkowych: numericPreprocess,
  });

export type InsertProject = z.infer<typeof insertProjectSchema>;
export type Project = typeof projects.$inferSelect;

// Project history table for tracking changes
export const projectHistory = pgTable("project_history", {
  id: varchar("id").primaryKey(),
  projectId: varchar("project_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertProjectHistorySchema = createInsertSchema(projectHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertProjectHistory = z.infer<typeof insertProjectHistorySchema>;
export type ProjectHistory = typeof projectHistory.$inferSelect;

// Cost type options (Rodzaj)
export const costTypeOptions = ["Materiał", "Koszt"] as const;
export type CostType = typeof costTypeOptions[number];

// Currency options (ISO 4217)
export const currencyOptions = ["PLN", "EUR", "USD", "GBP", "CHF", "NOK", "SEK", "DKK", "CZK", "HUF", "RON", "BGN"] as const;
export type Currency = typeof currencyOptions[number];

// Currency display names in Polish
export const currencyDisplayNames: Record<Currency, string> = {
  PLN: "Polski zloty",
  EUR: "Euro",
  USD: "Dolar amerykanski",
  GBP: "Funt brytyjski",
  CHF: "Frank szwajcarski",
  NOK: "Korona norweska",
  SEK: "Korona szwedzka",
  DKK: "Korona dunska",
  CZK: "Korona czeska",
  HUF: "Forint wegierski",
  RON: "Lej rumunski",
  BGN: "Lew bulgarski",
};

// Currency flags
export const currencyFlags: Record<Currency, string> = {
  PLN: "🇵🇱",
  EUR: "🇪🇺",
  USD: "🇺🇸",
  GBP: "🇬🇧",
  CHF: "🇨🇭",
  NOK: "🇳🇴",
  SEK: "🇸🇪",
  DKK: "🇩🇰",
  CZK: "🇨🇿",
  HUF: "🇭🇺",
  RON: "🇷🇴",
  BGN: "🇧🇬",
};

// Country name (Polish) to currency mapping for search
export const countryCurrencyMapping: Record<string, Currency> = {
  // Eurozone countries
  "Austria": "EUR",
  "Belgia": "EUR",
  "Cypr": "EUR",
  "Estonia": "EUR",
  "Finlandia": "EUR",
  "Francja": "EUR",
  "Niemcy": "EUR",
  "Grecja": "EUR",
  "Irlandia": "EUR",
  "Wlochy": "EUR",
  "Lotwa": "EUR",
  "Litwa": "EUR",
  "Luksemburg": "EUR",
  "Malta": "EUR",
  "Holandia": "EUR",
  "Portugalia": "EUR",
  "Slowacja": "EUR",
  "Slowenia": "EUR",
  "Hiszpania": "EUR",
  "Chorwacja": "EUR",
  // Other European currencies
  "Wielka Brytania": "GBP",
  "Anglia": "GBP",
  "Szwajcaria": "CHF",
  "Norwegia": "NOK",
  "Szwecja": "SEK",
  "Dania": "DKK",
  "Czechy": "CZK",
  "Wegry": "HUF",
  "Polska": "PLN",
  "Rumunia": "RON",
  "Bulgaria": "BGN",
  "USA": "USD",
  "Stany Zjednoczone": "USD",
};

// Costs table schema
export const costs = pgTable("costs", {
  id: varchar("id").primaryKey(),
  nrPorzadkowy: text("nr_porzadkowy").notNull(),
  nrVat: text("nr_vat").notNull(),
  nipDostawcy: text("nip_dostawcy"),
  supplierId: varchar("supplier_id"),  // FK to suppliers - used for nazwaDostawcy via JOIN
  krajDostawcy: text("kraj_dostawcy").$type<EuropeanCountry>(),
  vatRateId: varchar("vat_rate_id"),
  stawkaVat: numeric("stawka_vat"),
  netto: numeric("netto"),
  vat: numeric("vat"),
  zaokraglenie: numeric("zaokraglenie"),
  brutto: numeric("brutto"),
  dataZaplaty: text("data_zaplaty"),
  terminDni: integer("termin_dni"),
  rodzaj: text("rodzaj").notNull().$type<CostType>(),
  terminPlatnosci: text("termin_platnosci"),
  pozostalo: numeric("pozostalo"),
  zaplacono: boolean("zaplacono").notNull(),
  // Currency fields
  dataWystawieniaFV: text("data_wystawienia_fv"),
  waluta: text("waluta").notNull().default("PLN").$type<Currency>(),
  kursNbp: numeric("kurs_nbp"),
  wartoscNettoPln: numeric("wartosc_netto_pln"),
  wartoscBruttoPln: numeric("wartosc_brutto_pln"),
  tabelaNbp: text("tabela_nbp"),
  dataPlatnosciBackup: text("data_platnosci_backup"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema for costs (nrPorzadkowy optional for CSV imports)
export const insertCostSchema = createInsertSchema(costs).omit({
  id: true,
  nrPorzadkowy: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  nrPorzadkowy: z.string().regex(costNrRegex, "Nr porządkowy musi być w formacie K1, K2, itp.").optional(),
  supplierId: z.string().nullable().optional(),
  // Numeric preprocessing for all numeric fields
  stawkaVat: numericPreprocess,
  netto: numericPreprocess,
  vat: numericPreprocess,
  zaokraglenie: numericPreprocess,
  brutto: numericPreprocess,
  pozostalo: numericPreprocess,
  kursNbp: numericPreprocess,
  wartoscNettoPln: numericPreprocess,
  wartoscBruttoPln: numericPreprocess,
});

export type InsertCost = z.infer<typeof insertCostSchema>;
// Cost type extends table schema with nazwaDostawcy derived from suppliers table via JOIN
export type Cost = typeof costs.$inferSelect & { nazwaDostawcy: string | null };

// MPK Allocations table (separate table for flexible cost allocation across projects)
export const costMpkAllocations = pgTable("cost_mpk_allocations", {
  id: varchar("id").primaryKey(),
  costId: varchar("cost_id").notNull(),
  mpk: text("mpk"),
  mpkOpis: text("mpk_opis"),
  mpkKwota: numeric("mpk_kwota"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertCostMpkAllocationSchema = createInsertSchema(costMpkAllocations).omit({
  id: true,
  createdAt: true,
}).extend({
  mpkKwota: numericPreprocess,
});

export type InsertCostMpkAllocation = z.infer<typeof insertCostMpkAllocationSchema>;
export type CostMpkAllocation = typeof costMpkAllocations.$inferSelect;

// Cost history table for tracking changes
export const costHistory = pgTable("cost_history", {
  id: varchar("id").primaryKey(),
  costId: varchar("cost_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertCostHistorySchema = createInsertSchema(costHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertCostHistory = z.infer<typeof insertCostHistorySchema>;
export type CostHistory = typeof costHistory.$inferSelect;

// Payment status options (Czy zapłacono?)
export const paymentStatusOptions = ["Tak", "Nie"] as const;
export type PaymentStatus = typeof paymentStatusOptions[number];

// Preprocessor for payment status: accepts boolean or string, converts to "Tak"/"Nie"
const paymentStatusPreprocess = z.preprocess(
  (val) => {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "boolean") return val ? "Tak" : "Nie";
    if (typeof val === "string") {
      const lower = val.toLowerCase().trim();
      if (["tak", "true", "1", "yes", "t", "y"].includes(lower)) return "Tak";
      if (["nie", "false", "0", "no", "n", "f"].includes(lower)) return "Nie";
      return val; // Pass through for validation
    }
    return val;
  },
  z.enum(paymentStatusOptions).nullable().optional()
);

// Revenues table schema
export const revenues = pgTable("revenues", {
  id: varchar("id").primaryKey(),
  nrPorzadkowy: text("nr_porzadkowy").notNull(),
  nrFvat: text("nr_fvat"),
  nrPorzadkowyKlienta: text("nr_porzadkowy_klienta"),
  nazwaKlienta: text("nazwa_klienta"),
  nipKlienta: text("nip_klienta"),
  vatRateId: varchar("vat_rate_id"),
  kwotaNetto: numeric("kwota_netto"),
  kwotaVat: numeric("kwota_vat"),
  kwotaBrutto: numeric("kwota_brutto"),
  dataWystawienia: text("data_wystawienia"),
  terminPlatnosci: integer("termin_platnosci"),
  dataZaplaty: text("data_zaplaty"),
  nrPorzadkowyProjektu: text("nr_porzadkowy_projektu"),
  czyZaplacono: text("czy_zaplacono").$type<PaymentStatus>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema for revenues (nrPorzadkowy optional for CSV imports)
export const insertRevenueSchema = createInsertSchema(revenues).omit({
  id: true,
  nrPorzadkowy: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  nrPorzadkowy: z.string().regex(revenueNrRegex, "Nr porządkowy musi być w formacie FV1, FV2, itp.").optional(),
  vatRateId: z.string().nullable().optional(),
  // Numeric preprocessing for CSV import (converts numbers to strings, normalizes decimals)
  kwotaNetto: numericPreprocess,
  kwotaVat: numericPreprocess,
  kwotaBrutto: numericPreprocess,
  // Payment status preprocessing (converts booleans and various string formats to "Tak"/"Nie")
  czyZaplacono: paymentStatusPreprocess,
});

export type InsertRevenue = z.infer<typeof insertRevenueSchema>;
// Base Revenue type (from database)
export type RevenueBase = typeof revenues.$inferSelect;
// Revenue with joined project data (for API responses)
export type Revenue = RevenueBase & {
  nazwaProjektu?: string | null;
};

// Revenue history table for tracking changes
export const revenueHistory = pgTable("revenue_history", {
  id: varchar("id").primaryKey(),
  revenueId: varchar("revenue_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertRevenueHistorySchema = createInsertSchema(revenueHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertRevenueHistory = z.infer<typeof insertRevenueHistorySchema>;
export type RevenueHistory = typeof revenueHistory.$inferSelect;

// European country options (ISO 3166-1 alpha-2 codes)
export const europeanCountryOptions = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE", "NO", "CH", "GB"
] as const;
export type EuropeanCountry = typeof europeanCountryOptions[number];

// Country display names in Polish
export const countryDisplayNames: Record<EuropeanCountry, string> = {
  AT: "Austria", BE: "Belgia", BG: "Bułgaria", HR: "Chorwacja", CY: "Cypr",
  CZ: "Czechy", DK: "Dania", EE: "Estonia", FI: "Finlandia", FR: "Francja",
  DE: "Niemcy", GR: "Grecja", HU: "Węgry", IE: "Irlandia", IT: "Włochy",
  LV: "Łotwa", LT: "Litwa", LU: "Luksemburg", MT: "Malta", NL: "Holandia",
  PL: "Polska", PT: "Portugalia", RO: "Rumunia", SK: "Słowacja", SI: "Słowenia",
  ES: "Hiszpania", SE: "Szwecja", NO: "Norwegia", CH: "Szwajcaria", GB: "Wielka Brytania",
};

// Country flag emojis
export const countryFlags: Record<EuropeanCountry, string> = {
  AT: "🇦🇹", BE: "🇧🇪", BG: "🇧🇬", HR: "🇭🇷", CY: "🇨🇾", CZ: "🇨🇿",
  DK: "🇩🇰", EE: "🇪🇪", FI: "🇫🇮", FR: "🇫🇷", DE: "🇩🇪", GR: "🇬🇷",
  HU: "🇭🇺", IE: "🇮🇪", IT: "🇮🇹", LV: "🇱🇻", LT: "🇱🇹", LU: "🇱🇺",
  MT: "🇲🇹", NL: "🇳🇱", PL: "🇵🇱", PT: "🇵🇹", RO: "🇷🇴", SK: "🇸🇰",
  SI: "🇸🇮", ES: "🇪🇸", SE: "🇸🇪", NO: "🇳🇴", CH: "🇨🇭", GB: "🇬🇧",
};

// Suppliers table schema
export const suppliers = pgTable("suppliers", {
  id: varchar("id").primaryKey(),
  nazwa: text("nazwa").notNull(),
  nip: text("nip"),
  kraj: text("kraj").$type<EuropeanCountry>(),
  archived: boolean("archived").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertSupplierSchema = createInsertSchema(suppliers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const supplierFormSchema = insertSupplierSchema.extend({
  nazwa: z.string().min(1, "Nazwa dostawcy jest wymagana"),
  nip: z.string().optional().nullable(),
  kraj: z.enum(europeanCountryOptions).optional().nullable(),
});

export type InsertSupplier = z.infer<typeof insertSupplierSchema>;
export type Supplier = typeof suppliers.$inferSelect;

// Supplier history table
export const supplierHistory = pgTable("supplier_history", {
  id: varchar("id").primaryKey(),
  supplierId: varchar("supplier_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertSupplierHistorySchema = createInsertSchema(supplierHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertSupplierHistory = z.infer<typeof insertSupplierHistorySchema>;
export type SupplierHistory = typeof supplierHistory.$inferSelect;

// Connection type options for external project links
export const connectionTypeOptions = ["GoogleDrive", "ClickUp", "Remato"] as const;
export type ConnectionType = typeof connectionTypeOptions[number];

// Project connections table - stores external links per project
export const projectConnections = pgTable("project_connections", {
  id: varchar("id").primaryKey(),
  projectId: varchar("project_id").notNull(),
  connectionType: text("connection_type").notNull().$type<ConnectionType>(),
  name: text("name").notNull(),
  url: text("url").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertProjectConnectionSchema = createInsertSchema(projectConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertProjectConnection = z.infer<typeof insertProjectConnectionSchema>;
export type ProjectConnection = typeof projectConnections.$inferSelect;

// App settings table - stores global configuration
export const appSettings = pgTable("app_settings", {
  key: varchar("key", { length: 100 }).primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertAppSettingSchema = createInsertSchema(appSettings);

export type InsertAppSetting = z.infer<typeof insertAppSettingSchema>;
export type AppSetting = typeof appSettings.$inferSelect;

// Employees table - stores employee/worker information
export const employees = pgTable("employees", {
  id: varchar("id").primaryKey(),
  imie: text("imie").notNull(),
  nazwisko: text("nazwisko"),
  rola: text("rola"),
  color: text("color"), // hex color code (e.g., "#FF5500")
  archived: boolean("archived").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertEmployeeSchema = createInsertSchema(employees).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmployee = z.infer<typeof insertEmployeeSchema>;
export type Employee = typeof employees.$inferSelect;

// Configurable options table - stores editable dropdown options
export const configurableOptions = pgTable("configurable_options", {
  id: varchar("id").primaryKey(),
  category: text("category").notNull(), // e.g., 'projectType', 'projectStatus', 'responsiblePerson'
  value: text("value").notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  archived: boolean("archived").default(false).notNull(),
  color: text("color"), // hex color code (e.g., "#FF5500")
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertConfigurableOptionSchema = createInsertSchema(configurableOptions).omit({
  id: true,
  createdAt: true,
});

export type InsertConfigurableOption = z.infer<typeof insertConfigurableOptionSchema>;
export type ConfigurableOption = typeof configurableOptions.$inferSelect;

// VAT rates table - stores VAT rates per country
export const vatRates = pgTable("vat_rates", {
  id: varchar("id").primaryKey(),
  countryCode: text("country_code").notNull().$type<EuropeanCountry>(),
  rate: numeric("rate").notNull(),
  archived: boolean("archived").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVatRateSchema = createInsertSchema(vatRates).omit({
  id: true,
  createdAt: true,
});

// Schema with validation for creating VAT rates
export const vatRateFormSchema = insertVatRateSchema.extend({
  countryCode: z.enum(europeanCountryOptions, { required_error: "Kraj jest wymagany" }),
  rate: z.string().refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num > 0 && num <= 100;
  }, { message: "Stawka musi być liczbą między 0 a 100" }),
});

export type InsertVatRate = z.infer<typeof insertVatRateSchema>;
export type VatRate = typeof vatRates.$inferSelect;
